import React from 'react';
import HeroCarousel from '../../components/HeroCarousel';
import { Link } from 'react-router-dom';
import { Globe, Target, Users, Calendar, Clock, MapPin, Mail, Image as ImageIcon, Share2, ArrowUpRight, Flame } from 'lucide-react';

// ARCHETYPE 3: NGODECA — Inspired by the NGODECA Grand Launching flyer
// Bold outlined headings, terracotta/burnt orange, golden yellow badges, organic wave shapes
const HearthTemplate = ({ association: a, activities, theme: t, onShare }) => {
  const C = {
    cream: '#FFF8F0',
    terracotta: t.primary || '#C25A3F',
    gold: t.secondary || '#FFB300',
    dark: '#1A1A1A',
    warmGray: '#F5EDE4',
  };

  return (
    <div style={{ backgroundColor: C.cream, color: C.dark }} className="min-h-screen">
      {/* Navbar — warm, bold */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b shadow-sm" style={{ backgroundColor: C.cream, borderColor: `${C.terracotta}20` }} data-testid="association-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <img src={a.logo_url || a.image_url} alt="" className="w-10 h-10 rounded-lg object-cover border-2" style={{ borderColor: C.terracotta }} />
            <span className="text-xl font-black tracking-tight" style={{ fontFamily: "'Bangers', cursive", color: C.terracotta, letterSpacing: '0.04em' }}>{a.short_name}</span>
          </div>
          <div className="hidden md:flex gap-6">
            {['About', 'Heritage', 'Leadership', 'Events', 'Gallery'].map(s => (
              <a key={s} href={`#${s.toLowerCase()}`} className="text-sm font-bold uppercase tracking-wider transition-colors hover:opacity-70" style={{ color: C.dark }}>{s}</a>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onShare} className="p-2 hover:opacity-70" data-testid="share-btn"><Share2 className="w-4 h-4" /></button>
            <Link to="/" className="text-xs font-bold px-3 py-1.5 rounded-full text-white" style={{ backgroundColor: C.terracotta }} data-testid="cca-return-badge">CCA Home</Link>
          </div>
        </div>
      </nav>

      {/* Hero — Flyer-inspired: bold outlined text over cultural photo */}
      <section id="about" className="pt-14 relative" data-testid="association-hero">
        {/* Full-width cultural photo hero */}
        <div className="relative min-h-[75vh] overflow-hidden flex items-center justify-center">
          {a.hero_images?.length > 0
            ? <div className="absolute inset-0"><HeroCarousel images={a.hero_images} className="w-full h-full" /></div>
            : <div className="absolute inset-0" style={{ backgroundColor: `${C.terracotta}20` }} />
          }
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />

          {/* Bold flyer-style text overlay */}
          <div className="relative text-center px-4 py-12">
            {/* Region badge */}
            <div className="inline-block px-5 py-1.5 rounded-full mb-4 shadow-lg" style={{ backgroundColor: C.gold, color: C.dark }}>
              <span className="text-xs font-black uppercase tracking-widest">{a.region} Region</span>
            </div>

            {/* Big outlined title — flyer style */}
            <h1 className="text-6xl sm:text-7xl lg:text-9xl font-black text-white leading-[0.9] mb-2" style={{
              fontFamily: "'Bangers', cursive",
              letterSpacing: '0.03em',
              WebkitTextStroke: '2px rgba(0,0,0,0.5)',
              textShadow: '3px 3px 0 rgba(0,0,0,0.3), -1px -1px 0 rgba(0,0,0,0.2)',
            }} data-testid="association-name">
              {a.short_name}
            </h1>

            {/* Subtitle in gold — like "COMMUNITY IN CANADA" on the flyer */}
            <p className="text-2xl sm:text-3xl font-black uppercase tracking-wide" style={{
              fontFamily: "'Bangers', cursive",
              color: C.gold,
              textShadow: '2px 2px 0 rgba(0,0,0,0.4)',
              letterSpacing: '0.05em',
            }}>
              {a.name}
            </p>
          </div>
        </div>

        {/* Organic terracotta wave divider */}
        <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="w-full -mt-1 block" style={{ height: '60px' }}>
          <path d="M0,40 C360,100 720,0 1080,60 C1260,80 1380,30 1440,50 L1440,100 L0,100 Z" fill={C.terracotta} />
        </svg>
      </section>

      {/* Cultural highlights strip on terracotta */}
      {a.cultural_highlights && (
        <section className="py-8 px-6" style={{ backgroundColor: C.terracotta }} data-testid="cultural-highlights-banner">
          <div className="max-w-4xl mx-auto text-center">
            <Flame className="w-6 h-6 mx-auto mb-3 text-white/80" />
            <p className="text-white text-lg font-medium leading-relaxed italic" style={{ fontFamily: "'Quicksand', sans-serif" }}>
              {a.cultural_highlights}
            </p>
          </div>
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="w-full mt-8 -mb-8 block" style={{ height: '40px' }}>
            <path d="M0,20 C480,60 960,0 1440,30 L1440,60 L0,60 Z" fill={C.cream} />
          </svg>
        </section>
      )}

      {/* About + Mission — with yellow badge accents */}
      <section className="py-20 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: C.cream }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4" style={{ backgroundColor: C.gold, color: C.dark }}>About Us</span>
            <h2 className="text-4xl sm:text-5xl font-black" style={{ fontFamily: "'Bangers', cursive", color: C.terracotta, letterSpacing: '0.03em' }}>
              Who We Are
            </h2>
          </div>
          <p className="text-lg leading-relaxed text-center max-w-3xl mx-auto mb-12" style={{ color: `${C.dark}aa`, fontFamily: "'Quicksand', sans-serif" }} data-testid="association-description">{a.description}</p>
          {a.origin && <p className="text-center text-sm mb-12 flex items-center justify-center gap-2" style={{ color: `${C.dark}66` }}><Globe className="w-4 h-4" style={{ color: C.terracotta }} />{a.origin}</p>}

          <div className="grid md:grid-cols-2 gap-6">
            {/* Mission card */}
            <div className="p-6 sm:p-8 rounded-2xl border-2" style={{ borderColor: C.terracotta, backgroundColor: `${C.terracotta}08` }}>
              <div className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4" style={{ backgroundColor: C.gold, color: C.dark }}>Our Mission</div>
              <p className="leading-relaxed" style={{ color: `${C.dark}88`, fontFamily: "'Quicksand', sans-serif" }} data-testid="association-mission">{a.mission}</p>
            </div>
            {/* Values card */}
            <div className="p-6 sm:p-8 rounded-2xl" style={{ backgroundColor: C.terracotta }}>
              <h3 className="text-lg font-bold text-white mb-4 uppercase tracking-wider" style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.05em' }}>Core Values</h3>
              <div className="flex flex-wrap gap-2" data-testid="association-values">
                {(a.values || []).map((v, i) => (
                  <span key={i} className="px-4 py-2 rounded-full text-sm font-bold border-2 border-white/40 text-white" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>{v}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Heritage — warm background with wave */}
      <section id="heritage" className="relative">
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="w-full block" style={{ height: '40px' }}>
          <path d="M0,30 C360,0 720,60 1080,20 C1260,5 1380,40 1440,25 L1440,60 L0,60 Z" fill={C.warmGray} />
        </svg>
        <div className="py-16 sm:py-20 px-4 sm:px-6" style={{ backgroundColor: C.warmGray }}>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4" style={{ backgroundColor: C.gold, color: C.dark }}>Heritage</span>
              <h2 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "'Bangers', cursive", color: C.terracotta }}>Cultural Heritage</h2>
            </div>
            <p className="text-lg leading-relaxed text-center" style={{ color: `${C.dark}77`, fontFamily: "'Quicksand', sans-serif" }} data-testid="cultural-background">{a.cultural_background || a.cultural_highlights}</p>

            <div className="grid sm:grid-cols-3 gap-4 mt-10">
              {[{ t: 'Traditional Attire', d: 'Members proudly wear traditional garments reflecting cultural heritage during ceremonies.' }, { t: 'Traditional Dance', d: 'Cultural dances passed down through generations with pride.' }, { t: 'Cuisine', d: 'Traditional dishes lovingly prepared and shared at community gatherings.' }].map((item, i) => (
                <div key={i} className="p-5 rounded-2xl border-2 text-center" style={{ borderColor: `${C.terracotta}20`, backgroundColor: C.cream }}>
                  <h4 className="font-bold text-lg mb-2" style={{ fontFamily: "'Bangers', cursive", color: C.terracotta, letterSpacing: '0.03em' }}>{item.t}</h4>
                  <p className="text-sm" style={{ color: `${C.dark}66`, fontFamily: "'Quicksand', sans-serif" }}>{item.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="w-full block" style={{ height: '40px' }}>
          <path d="M0,0 L1440,0 L1440,30 C1080,60 720,0 360,40 C180,55 60,20 0,35 Z" fill={C.warmGray} />
        </svg>
      </section>

      {/* Leadership */}
      <section id="leadership" className="py-20 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: C.cream }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4" style={{ backgroundColor: C.gold, color: C.dark }}>Leadership</span>
            <h2 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "'Bangers', cursive", color: C.terracotta }}>Our Leaders</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* President */}
            <div className="rounded-2xl overflow-hidden border-2 text-center" style={{ borderColor: C.terracotta }}>
              <div className="h-3" style={{ backgroundColor: C.terracotta }} />
              <div className="p-6">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden border-3" style={{ border: `3px solid ${C.gold}` }}>
                  {a.president_photo_url ? <img src={a.president_photo_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: `${C.terracotta}15` }}><span className="text-3xl font-bold" style={{ color: C.terracotta }}>{(a.president_name || '?')[0]}</span></div>}
                </div>
                <h4 className="font-bold text-lg" style={{ fontFamily: "'Quicksand', sans-serif" }}>{a.president_name || 'Vacant'}</h4>
                <span className="inline-block px-3 py-0.5 rounded-full text-xs font-bold mt-1" style={{ backgroundColor: C.gold, color: C.dark }}>President</span>
                {a.president_email && <p className="text-xs mt-2 flex items-center justify-center gap-1" style={{ color: `${C.dark}55` }}><Mail className="w-3 h-3" />{a.president_email}</p>}
              </div>
            </div>
            {(a.leadership || []).map((l, i) => (
              <div key={l.id || i} className="rounded-2xl overflow-hidden border text-center" style={{ borderColor: `${C.terracotta}25` }}>
                <div className="p-6">
                  <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden" style={{ backgroundColor: `${C.terracotta}10` }}>
                    {l.photo_url ? <img src={l.photo_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><span className="text-2xl" style={{ color: `${C.dark}30` }}>{(l.name || '?')[0]}</span></div>}
                  </div>
                  <h4 className="font-bold" style={{ fontFamily: "'Quicksand', sans-serif" }}>{l.name}</h4>
                  <p className="text-sm mt-1" style={{ color: C.terracotta }}>{l.role}</p>
                </div>
              </div>
            ))}
            {(!a.leadership || a.leadership.length === 0) && ['Vice President', 'Secretary'].map(r => (
              <div key={r} className="rounded-2xl border border-dashed p-6 text-center" style={{ borderColor: `${C.terracotta}20` }}>
                <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: `${C.terracotta}08` }}><Users className="w-6 h-6" style={{ color: `${C.dark}20` }} /></div>
                <h4 className="font-semibold" style={{ color: `${C.dark}30` }}>{r}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Events — terracotta section with wave */}
      <section id="events" className="relative">
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="w-full block" style={{ height: '40px' }}>
          <path d="M0,30 C480,0 960,60 1440,20 L1440,60 L0,60 Z" fill={C.terracotta} />
        </svg>
        <div className="py-16 sm:py-20 px-4 sm:px-6" style={{ backgroundColor: C.terracotta }}>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-black text-white" style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.03em' }}>Events</h2>
            </div>
            {activities.length > 0 ? (
              <div className="space-y-4">
                {activities.map(ev => (
                  <div key={ev.id} className="p-5 rounded-2xl flex flex-col sm:flex-row gap-4" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }} data-testid={`event-${ev.id}`}>
                    <div className="flex-shrink-0 w-16 h-16 rounded-xl flex flex-col items-center justify-center" style={{ backgroundColor: C.gold, color: C.dark }}>
                      <span className="text-xl font-black">{ev.date?.split('-')[2]}</span>
                      <span className="text-[10px] uppercase font-bold">{ev.date && new Date(ev.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-lg text-white">{ev.title}</h4>
                      <p className="text-sm text-white/60 mt-1">{ev.description}</p>
                      <div className="flex gap-3 mt-2 text-xs text-white/40">
                        {ev.time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{ev.time}</span>}
                        {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 rounded-2xl" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
                <Calendar className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/50">No events scheduled yet.</p>
              </div>
            )}
          </div>
        </div>
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="w-full block" style={{ height: '40px' }}>
          <path d="M0,0 L1440,0 L1440,40 C960,0 480,60 0,25 Z" fill={C.terracotta} />
        </svg>
      </section>

      {/* Gallery */}
      <section id="gallery" className="py-20 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: C.cream }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4" style={{ backgroundColor: C.gold, color: C.dark }}>Gallery</span>
            <h2 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "'Bangers', cursive", color: C.terracotta }}>Photo Gallery</h2>
          </div>
          {a.gallery_images?.length > 0 ? (
            <div className="columns-2 md:columns-3 gap-4 space-y-4">
              {a.gallery_images.map((img, i) => (
                <div key={i} className="break-inside-avoid rounded-2xl overflow-hidden border-2 shadow-md" style={{ borderColor: `${C.terracotta}15` }} data-testid={`gallery-image-${i}`}>
                  <img src={img.url} alt={img.caption || ''} className="w-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 rounded-2xl border-2 border-dashed" style={{ borderColor: `${C.terracotta}20` }}>
              <ImageIcon className="w-12 h-12 mx-auto mb-3" style={{ color: `${C.dark}15` }} />
              <p style={{ color: `${C.dark}40` }}>Gallery coming soon.</p>
            </div>
          )}
        </div>
      </section>

      {/* Videos */}
      <section className="py-20 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: C.warmGray }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4" style={{ backgroundColor: C.gold, color: C.dark }}>Videos</span>
            <h2 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "'Bangers', cursive", color: C.terracotta }}>Cultural Videos</h2>
          </div>
          {(a.youtube_videos || []).length > 0 ? (
            <div className="grid md:grid-cols-2 gap-6">
              {a.youtube_videos.map((v, i) => (
                <div key={i} className="rounded-2xl overflow-hidden border-2 shadow-md" style={{ borderColor: `${C.terracotta}20` }} data-testid={`video-${i}`}>
                  <div className="aspect-video"><iframe src={`https://www.youtube.com/embed/${v.video_id}`} title={v.title || ''} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>
                  {v.title && <div className="p-3" style={{ backgroundColor: C.cream }}><h4 className="font-bold text-sm" style={{ fontFamily: "'Quicksand', sans-serif" }}>{v.title}</h4></div>}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 rounded-2xl border-2 border-dashed" style={{ borderColor: `${C.terracotta}15` }}>
              <p style={{ color: `${C.dark}40` }}>No videos yet. Cultural videos will appear here.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer — terracotta with wave */}
      <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="w-full block" style={{ height: '40px' }}>
        <path d="M0,30 C360,0 720,60 1080,20 C1260,5 1380,40 1440,25 L1440,60 L0,60 Z" fill={C.terracotta} />
      </svg>
      <footer className="py-16 text-center" style={{ backgroundColor: C.terracotta }}>
        <img src={a.logo_url || a.image_url} alt="" className="w-16 h-16 rounded-full mx-auto mb-4 object-cover border-3" style={{ border: `3px solid ${C.gold}` }} />
        <h3 className="text-3xl font-black text-white" style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.03em' }}>{a.short_name}</h3>
        <p className="text-white/70 text-sm mt-1 mb-2" style={{ fontFamily: "'Quicksand', sans-serif" }}>{a.name}</p>
        <p className="text-white font-bold text-sm italic mb-6" style={{ fontFamily: "'Quicksand', sans-serif" }}>Together We Are Stronger!</p>
        <div className="flex justify-center gap-6 text-sm text-white/50">
          <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">CCA Montreal <ArrowUpRight className="w-3 h-3" /></Link>
          <Link to="/associations" className="hover:text-white transition-colors">All Associations</Link>
          <Link to="/calendar" className="hover:text-white transition-colors">Events</Link>
        </div>
      </footer>
    </div>
  );
};

export default HearthTemplate;
